import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Linkedin, Twitter, Youtube, Instagram } from "lucide-react";
import PageHero from "@/components/site/PageHero";
import { Eyebrow, Reveal, GoldButton } from "@/components/site/motion";
import { IMAGES } from "@/data/content";
import { api } from "@/lib/api";
import { useSEO } from "@/hooks/useSEO";

const companyOptions = [
  "General",
  "Hapo Group",
  "Hapo Technology",
  "Hapo Cloud",
  "Hapo Media",
  "Hapo Payment Solutions",
  "Hapo Electronics",
  "Hapo Energy",
  "Hapo Intelligence",
  "Hapo Sound",
  "XR Africa",
  "Book the XR Studio",
  "Hapo Intelligence Demo",
  "Partnership Enquiry",
  "Careers",
];

const inputCls =
  "w-full border-b border-ink/20 bg-transparent py-3 text-sm text-ink placeholder:text-smoke focus:border-gold focus:outline-none transition-colors duration-300";

export default function Contact() {
  useSEO("Contact — Hapo Group", "Get in touch with Hapo Group — partnerships, investment, careers, or any of our nine companies.");
  const [searchParams] = useSearchParams();
  const regarding = searchParams.get("regarding");
  const initialCompany = companyOptions.includes(regarding)
    ? regarding
    : searchParams.get("topic") === "intel-demo"
      ? "Hapo Intelligence Demo"
      : "General";
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: initialCompany, message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  // NOTE: enquiries are stored in the app database and viewable at /admin.
  // Wire up email delivery (e.g. Formspree, Resend, or an edge function) before go-live.
  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.sendEnquiry(form);
      setSent(true);
      toast.success("Enquiry sent — we'll be in touch.");
    } catch {
      toast.error("Could not send your enquiry — please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <PageHero short eyebrow="Contact" lines={["Get in Touch."]} image={IMAGES.building} />

      <section className="bg-white px-6 py-24 lg:px-12 lg:py-[140px]" aria-label="Contact form and details">
        <div className="grid gap-16 lg:grid-cols-5">
          <div className="lg:col-span-3">
            <Eyebrow>Send a Message</Eyebrow>
            {sent ? (
              <div data-testid="contact-success-message">
                <Reveal className="mt-10 border-l-2 border-gold bg-mist p-10">
                  <h2 className="text-2xl font-bold tracking-tight text-ink">Thank you, {form.name.split(" ")[0]}.</h2>
                  <p className="mt-4 text-sm leading-loose text-smoke">
                    Your enquiry about <span className="font-semibold text-ink">{form.company}</span> has been received.
                    The right team will get back to you shortly.
                  </p>
                </Reveal>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-10 space-y-8" data-testid="contact-form">
                <div className="grid gap-8 sm:grid-cols-2">
                  <label className="block">
                    <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-smoke">Name *</span>
                    <input required value={form.name} onChange={update("name")} data-testid="contact-name-input" className={inputCls} placeholder="Your full name" />
                  </label>
                  <label className="block">
                    <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-smoke">Email *</span>
                    <input required type="email" value={form.email} onChange={update("email")} data-testid="contact-email-input" className={inputCls} placeholder="you@company.co.za" />
                  </label>
                </div>
                <div className="grid gap-8 sm:grid-cols-2">
                  <label className="block">
                    <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-smoke">Phone (optional)</span>
                    <input type="tel" value={form.phone} onChange={update("phone")} data-testid="contact-phone-input" className={inputCls} placeholder="+27 ..." />
                  </label>
                  <label className="block">
                    <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-smoke">Which company is this regarding?</span>
                    <select value={form.company} onChange={update("company")} data-testid="contact-company-select" className={`${inputCls} cursor-pointer`}>
                      {companyOptions.map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                <label className="block">
                  <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-smoke">Message *</span>
                  <textarea required rows={5} value={form.message} onChange={update("message")} data-testid="contact-message-input" className={`${inputCls} resize-none`} placeholder="How can we help?" />
                </label>
                <GoldButton type="submit" testId="contact-submit-button">
                  {sending ? "Sending..." : "Submit"}
                </GoldButton>
              </form>
            )}
          </div>

          <div className="lg:col-span-2">
            <Eyebrow>Group Details</Eyebrow>
            <div className="mt-10 space-y-8 border-t border-ink/10 pt-10">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-smoke">Registered Address</p>
                <p className="mt-2 text-sm leading-relaxed text-ink">
                  1 Bridgeway Road, Bridgeway Precinct, Century City, Cape Town
                </p>
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-smoke">Phone</p>
                <a
                  href="tel:+27780802462"
                  data-testid="contact-phone-link"
                  className="mt-2 inline-block text-sm text-ink underline decoration-gold underline-offset-4"
                >
                  +27 78 080 2462
                </a>
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-smoke">Email</p>
                <a
                  href="mailto:info@hapogroup.co.za"
                  data-testid="contact-email-link"
                  className="mt-2 inline-block text-sm text-ink underline decoration-gold underline-offset-4"
                >
                  info@hapogroup.co.za
                </a>
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-smoke">Follow</p>
                <div className="mt-4 flex gap-3">
                  {[
                    { icon: Linkedin, testId: "contact-social-linkedin", label: "LinkedIn" },
                    { icon: Twitter, testId: "contact-social-twitter", label: "X (Twitter)" },
                    { icon: Youtube, testId: "contact-social-youtube", label: "YouTube" },
                    { icon: Instagram, testId: "contact-social-instagram", label: "Instagram" },
                  ].map(({ icon: Icon, testId, label }) => (
                    <a
                      key={testId}
                      href="#"
                      aria-label={`${label} (placeholder)`}
                      data-testid={testId}
                      className="flex h-10 w-10 items-center justify-center rounded-full border border-ink/15 text-ink/60 transition-colors duration-300 hover:border-gold hover:text-gold"
                    >
                      <Icon className="h-4 w-4" />
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
